import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Button, Form, Input, Card, Space, Empty, Switch, Select, message, Spin, Modal } from 'antd';
import { DeleteOutlined, SearchOutlined } from '@ant-design/icons';
import { Role2, FilterGroup } from '../../../models';
import { apiClient, apiErrorMessage } from '../../../utils';
import { FilterSelector } from '../../../elements';
import { TargetSelector, SelectedPermission } from './target-selector';

interface PermissionRequest {
  id?: string;
  effect: 'ALLOW' | 'DENY';
  target: string;
  filter?: FilterGroup;
  isActive: boolean;
}

interface Permission2 {
  id: string;
  target: string;
  targetLabel?: string;
  effect: 'ALLOW' | 'DENY';
  filterGroup?: FilterGroup;
  conditionSchemes?: string[] | null;
  dataSetId?: string;
}

interface PaginatedPermissionsResponse {
  data: Permission2[];
  total: number;
  page: number;
  pageSize: number;
  hasMore: boolean;
}

export function Role2DetailsForm(props: { role?: Role2; onSaved?: () => void }) {
  const [submitting, setSubmitting] = useState(false);
  const [selectedPermissions, setSelectedPermissions] = useState<Map<string, SelectedPermission>>(new Map());
  const [targetModalVisible, setTargetModalVisible] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedKeys, setExpandedKeys] = useState<string[]>([]);
  const [selectedPermissionsSearch, setSelectedPermissionsSearch] = useState('');

  // Pagination and loading states
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [page, setPage] = useState(1);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const loadingRef = useRef(false);

  // Virtual scrolling constants
  const ITEM_HEIGHT = 53; // Approximate height of each row
  const BUFFER_SIZE = 10; // Number of items to render beyond visible area
  const MAX_DOM_ITEMS = 100; // Maximum items to keep in DOM

  const [visibleStartIndex, setVisibleStartIndex] = useState(0);
  const [visibleEndIndex, setVisibleEndIndex] = useState(MAX_DOM_ITEMS);
  const displayedPermissionCount = Math.max(props.role?.permissions2Count || 0, selectedPermissions.size);

  const [form] = Form.useForm();

  // Load permissions from backend
  const loadPermissions = useCallback(
    async (pageNum: number, append = false) => {
      if (!props.role?.id || loadingRef.current) return;

      loadingRef.current = true;
      setLoading(true);

      try {
        const response = await apiClient.get<PaginatedPermissionsResponse>(
          `authorization/roles/${props.role.id}/permissions`,
          {
            params: {
              page: pageNum,
              pageSize: 50,
              search: '',
            },
          },
        );

        setHasMore(response.data.hasMore);
        setPage(pageNum);

        // Convert loaded permissions to selected permissions
        setSelectedPermissions(prevPermissions => {
          const newPermissions = append ? new Map<string, SelectedPermission>(prevPermissions) : new Map<string, SelectedPermission>();
          for (const permission of response.data.data) {
            newPermissions.set(permission.target, {
              id: permission.id,
              target: permission.target,
              label: permission.targetLabel || permission.target,
              effect: permission.effect,
              filter: permission.filterGroup,
              filterEnabled: !!permission.filterGroup,
              conditionSchemes: permission.conditionSchemes || null,
              dataSetId: permission.dataSetId,
              isNew: false,
            });
          }
          return newPermissions;
        });

        if (!append) {
          setVisibleStartIndex(0);
          setVisibleEndIndex(MAX_DOM_ITEMS);
          if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollTop = 0;
          }
        }
      } catch (error) {
        apiErrorMessage(error);
      } finally {
        setLoading(false);
        loadingRef.current = false;
      }
    },
    [props.role?.id],
  );

  // Initialize form and load permissions on mount
  useEffect(() => {
    if (!props.role?.id) {
      return;
    }

    // Load permissions only when role ID changes
    setPage(1);
    loadPermissions(1, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.role?.id]);

  // Update form values when role data changes (without reloading permissions)
  useEffect(() => {
    if (!props.role) {
      return;
    }

    form.setFieldsValue({
      name: props.role?.name,
      description: props.role?.description,
    });
  }, [form, props.role]);

  // Get filtered permissions based on search
  const getFilteredPermissions = useCallback(() => {
    return Array.from(selectedPermissions.values()).filter(
      permission =>
        !selectedPermissionsSearch ||
        permission.label.toLowerCase().includes(selectedPermissionsSearch.toLowerCase()) ||
        permission.target.toLowerCase().includes(selectedPermissionsSearch.toLowerCase()),
    );
  }, [selectedPermissions, selectedPermissionsSearch]);

  // Handle scroll for lazy loading and virtual scrolling
  const handleScroll = useCallback(() => {
    if (!scrollContainerRef.current) return;

    const { scrollTop: currentScrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;

    // Calculate visible range for virtual scrolling
    const filteredPermissions = getFilteredPermissions();
    const startIndex = Math.max(0, Math.floor(currentScrollTop / ITEM_HEIGHT) - BUFFER_SIZE);
    const endIndex = Math.min(filteredPermissions.length, startIndex + MAX_DOM_ITEMS);

    setVisibleStartIndex(startIndex);
    setVisibleEndIndex(endIndex);

    // Load more when near bottom
    if (scrollHeight - currentScrollTop - clientHeight < 200 && hasMore && !loading && !loadingRef.current) {
      loadPermissions(page + 1, true);
    }
  }, [hasMore, loading, page, loadPermissions, getFilteredPermissions]);

  // Update permission effect
  const updatePermissionEffect = (targetKey: string, effect: 'ALLOW' | 'DENY') => {
    const newSelections = new Map(selectedPermissions);
    const permission = newSelections.get(targetKey);
    if (permission) {
      newSelections.set(targetKey, { ...permission, effect });
      setSelectedPermissions(newSelections);
    }
  };

  // Update permission filter
  const updatePermissionFilter = (targetKey: string, filter: FilterGroup | undefined) => {
    const newSelections = new Map(selectedPermissions);
    const permission = newSelections.get(targetKey);
    if (permission) {
      newSelections.set(targetKey, { ...permission, filter });
      setSelectedPermissions(newSelections);
    }
  };

  // Toggle filter enabled
  const toggleFilterEnabled = (targetKey: string, enabled: boolean) => {
    const newSelections = new Map(selectedPermissions);
    const permission = newSelections.get(targetKey);
    if (permission) {
      newSelections.set(targetKey, {
        ...permission,
        filterEnabled: enabled,
        filter: enabled ? permission.filter : undefined,
      });
      setSelectedPermissions(newSelections);
    }
  };

  // Remove permission (toggles deletion for existing, removes new ones)
  const removePermission = (targetKey: string) => {
    const newSelections = new Map(selectedPermissions);
    const permission = newSelections.get(targetKey);
    if (permission) {
      if (permission.id) {
        // Existing permission: toggle deleted state
        newSelections.set(targetKey, { ...permission, isDeleted: !permission.isDeleted });
      } else {
        // New permission: remove it entirely
        newSelections.delete(targetKey);
      }
      setSelectedPermissions(newSelections);
    }
  };

  const onEditFinish = (values: any) => {
    setSubmitting(true);

    // Get non-deleted permissions
    const activePermissions = Array.from(selectedPermissions.values()).filter(p => !p.isDeleted);

    // Validate that there is at least one permission
    if (activePermissions.length === 0) {
      apiErrorMessage({ message: 'En az bir yetki eklenmelidir' });
      setSubmitting(false);
      return;
    }

    // Convert selected permissions to API format
    const rolePermissions: PermissionRequest[] = activePermissions.map(permission => {
      const permissionRequest: PermissionRequest = {
        effect: permission.effect,
        target: permission.target,
        filter: permission.filterEnabled && permission.filter ? permission.filter : undefined,
        isActive: true,
      };

      // Include id if the permission already exists
      if (permission.id) {
        permissionRequest.id = permission.id;
      }

      return permissionRequest;
    });

    const body = {
      name: values.name,
      description: values.description,
      permissions2: rolePermissions,
    };

    apiClient
      .put(`authorization/roles/${props.role?.id}`, body)
      .then(() => {
        setSubmitting(false);
        // Reload permissions to clear isNew flags
        loadPermissions(1, false);
        message.success('Rol başarıyla kaydedildi.');
        if (props.onSaved) {
          props.onSaved();
        }
      })
      .catch((reason: any) => {
        setSubmitting(false);
        apiErrorMessage(reason);
      });
  };

  return (
    <Form layout="vertical" onFinish={onEditFinish} form={form}>
      <Form.Item
        label="İsim"
        name="name"
        wrapperCol={{ span: 16 }}
        rules={[
          { required: true, message: 'Zorunlu alan' },
          {
            pattern: /^[a-zA-Z0-9ıİğĞüÜşŞöÖçÇ][a-zA-Z0-9_\-ıİğĞüÜşŞöÖçÇ\s]*$/,
            message: 'Sadece harf, rakam, boşluk, - ve _ karakteri kullanılabilir',
          },
          { max: 100, message: 'En fazla 100 karakter' },
        ]}
      >
        <Input />
      </Form.Item>
      <Form.Item label="Açıklama" name="description" wrapperCol={{ span: 16 }} rules={[{ max: 400, message: 'En fazla 400 karakter' }]}>
        <Input.TextArea />
      </Form.Item>

      <Form.Item label="Yetkiler" required>
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          {/* Selected Permissions List - Always visible */}
          <Card
            title={
              selectedPermissions?.size === 0 ? null : (
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  {selectedPermissions.size > 0 && (
                    <Input
                      placeholder="Ara..."
                      prefix={<SearchOutlined />}
                      value={selectedPermissionsSearch}
                      onChange={e => setSelectedPermissionsSearch(e.target.value)}
                      allowClear
                      size="small"
                      style={{ width: '250px' }}
                      onClick={e => e.stopPropagation()}
                    />
                  )}
                  {selectedPermissions.size > 0 && (
                    <span
                      style={{
                        fontSize: '13px',
                        color: '#595959',
                      }}
                    >{`${displayedPermissionCount} yetki eklendi`}</span>
                  )}
                </div>
              )
            }
            extra={
              selectedPermissions.size === 0 ? null : (
                <Button type="primary" size="small" onClick={() => setTargetModalVisible(true)}>
                  + Yetki ekle
                </Button>
              )
            }
            size="small"
          >
            {selectedPermissions.size === 0 ? (
              <Empty description="Henüz yetki eklenmedi" image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ padding: '20px 0' }}>
                <Button type="primary" onClick={() => setTargetModalVisible(true)}>
                  + Yetki ekle
                </Button>
              </Empty>
            ) : (
              <div
                ref={scrollContainerRef}
                onScroll={handleScroll}
                style={{ maxHeight: '600px', overflowY: 'auto', position: 'relative' }}
              >
                {/* Table Header */}
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'minmax(350px, 2.5fr) 60px 70px minmax(200px, 2fr) 40px',
                    gap: '8px',
                    padding: '10px 8px',
                    background: '#fafafa',
                    borderBottom: '2px solid #e8e8e8',
                    fontSize: '12px',
                    fontWeight: 600,
                    color: '#666',
                    position: 'sticky',
                    top: 0,
                    zIndex: 2,
                  }}
                >
                  <div style={{ paddingLeft: '0' }}>Yetki</div>
                  <div style={{ textAlign: 'center' }}>İzin</div>
                  <div style={{ textAlign: 'center' }}>Filtre</div>
                  <div>Filtre seçimi</div>
                  <div></div>
                </div>

                {/* Virtual scroll container */}
                {(() => {
                  const filteredPermissions = getFilteredPermissions();
                  const totalHeight = filteredPermissions.length * ITEM_HEIGHT;
                  const visiblePermissions = filteredPermissions.slice(visibleStartIndex, visibleEndIndex);
                  const offsetY = visibleStartIndex * ITEM_HEIGHT;

                  return (
                    <div style={{ height: totalHeight, position: 'relative' }}>
                      {/* Visible items with offset */}
                      <div style={{ transform: `translateY(${offsetY}px)` }}>
                        {visiblePermissions.map((permission, index) => {
                          const actualIndex = visibleStartIndex + index;
                          let baseBackground = permission.isNew ? '#e6f7ff' : (actualIndex % 2 === 0 ? 'white' : '#fafafa');
                          let borderColor = permission.isNew ? '#1890ff' : (permission.isDeleted ? '#ff4d4f' : 'none');
                          if (permission.isDeleted) {
                            baseBackground = '#fff2f0';
                          }
                          return (
                            <div
                              key={permission.target}
                              style={{
                                display: 'grid',
                                gridTemplateColumns: 'minmax(350px, 2.5fr) 60px 70px minmax(200px, 2fr) 40px',
                                gap: '8px',
                                padding: '10px 8px',
                                alignItems: 'center',
                                background: baseBackground,
                                borderBottom: '1px solid #f0f0f0',
                                borderLeft: borderColor !== 'none' ? `3px solid ${borderColor}` : 'none',
                                opacity: permission.isDeleted ? 0.6 : 1,
                                transition: 'background 0.2s',
                                height: ITEM_HEIGHT,
                              }}
                              onMouseEnter={e => {
                                if (permission.isDeleted) {
                                  e.currentTarget.style.background = '#ffebe9';
                                } else if (permission.isNew) {
                                  e.currentTarget.style.background = '#d4ebf7';
                                } else {
                                  e.currentTarget.style.background = '#f5f5f5';
                                }
                              }}
                              onMouseLeave={e => (e.currentTarget.style.background = baseBackground)}
                            >
                              {/* Permission Name */}
                              <div
                                style={{
                                  fontSize: '13px',
                                  wordBreak: 'break-word',
                                  lineHeight: '1.4',
                                  paddingLeft: '0',
                                }}
                              >
                                {permission.label}
                              </div>

                              {/* Effect Selection - Select Dropdown */}
                              <Select
                                size="small"
                                value={permission.effect}
                                onChange={value => updatePermissionEffect(permission.target, value)}
                                style={{ width: '100%' }}
                                options={[
                                  {
                                    value: 'ALLOW',
                                    label: <span style={{ color: '#52c41a', fontSize: '16px' }}>✓</span>,
                                  },
                                  {
                                    value: 'DENY',
                                    label: <span style={{ color: '#ff4d4f', fontSize: '16px' }}>✕</span>,
                                  },
                                ]}
                              />

                              {/* Filter Toggle */}
                              <div
                                style={{
                                  display: 'flex',
                                  justifyContent: 'center',
                                }}
                              >
                                {permission.conditionSchemes && permission.conditionSchemes.length > 0 ? (
                                  <Switch
                                    size="small"
                                    checked={permission.filterEnabled || false}
                                    onChange={checked => toggleFilterEnabled(permission.target, checked)}
                                  />
                                ) : (
                                  <span style={{ fontSize: '11px', color: '#ccc' }}>—</span>
                                )}
                              </div>

                              {/* Filter Selection */}
                              <div style={{ minWidth: 0 }}>
                                {permission.conditionSchemes && permission.conditionSchemes.length > 0 && permission.filterEnabled ? (
                                  <FilterSelector
                                    value={permission.filter}
                                    size="small"
                                    dataSetId={permission.dataSetId}
                                    onChange={value => updatePermissionFilter(permission.target, value)}
                                  />
                                ) : (
                                  <span style={{ fontSize: '11px', color: '#ccc' }}>—</span>
                                )}
                              </div>

                              {/* Delete Button */}
                              <div style={{ display: 'flex', justifyContent: 'center' }}>
                                <Button
                                  type="text"
                                  size="small"
                                  danger
                                  icon={<DeleteOutlined />}
                                  onClick={() => removePermission(permission.target)}
                                  style={{
                                    padding: '4px',
                                    height: 'auto',
                                    minWidth: 'auto',
                                  }}
                                />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}

                {/* Loading indicator */}
                {loading && (
                  <div style={{ padding: '20px', textAlign: 'center' }}>
                    <Spin />
                  </div>
                )}
              </div>
            )}
          </Card>
        </Space>
      </Form.Item>

      <Form.Item>
        <Button type="primary" htmlType="submit" loading={submitting}>
          Kaydet
        </Button>
      </Form.Item>

      {/* Target Selector Modal */}
      <Modal
        title="Yetki seç"
        visible={targetModalVisible}
        onCancel={() => setTargetModalVisible(false)}
        closable={false}
        destroyOnClose
        footer={[
          <Button key="close" type="primary" onClick={() => setTargetModalVisible(false)}>
            Tamam
          </Button>,
        ]}
        width="70%"
      >
        <TargetSelector
          onPermissionsChange={setSelectedPermissions}
          searchQuery={searchQuery}
          onSearchQueryChange={setSearchQuery}
          expandedKeys={expandedKeys}
          onExpandedKeysChange={setExpandedKeys}
          totalPermissionCount={props.role?.permissions2Count}
          roleId={props.role?.id}
          currentPermissions={selectedPermissions}
        />
      </Modal>
    </Form>
  );
}
